import { apiFetch } from './client'

/**
 * s61 / E2 (decisao de Marcelo 19/10/2026; P1-P4 da s57) - organograma em caixas.
 *
 * Contrato: `GET /api/org-chart` (apps/api/routes/org_chart.py, permissao `organization_core.unit.read`) ->
 * unidades ATIVAS com os cargos de cada uma (pela AREA da descricao publicada; sem publicada, a do rascunho) e o
 * NUMERO de pessoas ativas por cargo -- sem nomes. Nomes: `GET /api/org-chart/jobs/{id}/people`, so com a permissao
 * `employee.read.org_chart_names` (RH e administradores; a API devolve `can_see_names`). Salario nunca.
 * `payroll_base_date` = data da folha de onde vieram as pessoas (E3), para o retrato nao parecer de hoje.
 */

export interface OrgChartJob {
  jobId: string
  jobName: string
  /** Pessoas ATIVAS do cargo em toda a empresa (qualquer lotacao). */
  people: number
  /** s60c / O3: cargo-chefe do cabecalho vigente (publicada; sem publicada, rascunho). */
  reportsToJobId: string | null
  reportsToJobName: string | null
  /** s60c / O2b: o rascunho aberto leva o cargo para OUTRA area (so muda o desenho depois de publicar). */
  pendingAreaName: string | null
  /** s62 / C3b-2 (ADR-0032): funcao gravada no CARGO e a faixa dela (ausente/null = sem funcao: a tela sugere). */
  functionId?: string | null
  functionName?: string | null
  bandId?: string | null
}

/** s60c / O2: gente LOTADA nesta unidade cujo cargo tem a area em outra unidade (ex.: Auxiliar em MP1). */
export interface OrgChartStaffed {
  jobId: string
  jobName: string
  people: number
}

export interface OrgChartUnit {
  id: string
  name: string
  parentId: string | null
  jobs: OrgChartJob[]
  staffed: OrgChartStaffed[]
  /** Pessoas ATIVAS lotadas NESTA unidade (qualquer cargo, ou sem cargo). */
  peopleHere: number
  /** s62 / C3b-2 (X3): area de ASSESSORIA -- sai da arvore do mapa e vai para a coluna lateral. */
  isAdvisory?: boolean
}

/** s62 / C3b-2: faixa da empresa (linha do organograma de cargos) e funcao do catalogo. */
export interface OrgChartBand {
  id: string
  name: string
  position: number
}

export interface OrgChartFunction {
  id: string
  name: string
  bandId: string
}

export interface OrgChart {
  units: OrgChartUnit[]
  jobsWithoutArea: OrgChartJob[]
  peopleWithoutJob: number
  payrollBaseDate: string | null
  canSeeNames: boolean
  /** s62 / C3b-2: faixas em ordem e funcoes da empresa (API anterior: ausentes -> faixas fixas da metodologia). */
  bands?: OrgChartBand[]
  functions?: OrgChartFunction[]
}

export interface OrgChartNode {
  unit: OrgChartUnit
  children: OrgChartNode[]
  /** Pessoas LOTADAS na unidade e em tudo abaixo dela (s60c / O2). */
  total: number
  /** 0 = topo (DIRECAO); 1 = gerencias; 2 = areas; 3+ = subareas (cores C1). */
  depth: number
}

interface JobResp {
  job_id: string
  job_name: string
  people: number
  reports_to_job_id?: string | null
  reports_to_job_name?: string | null
  pending_area_name?: string | null
  function_id?: string | null
  function_name?: string | null
  band_id?: string | null
}

interface StaffedResp {
  job_id: string
  job_name: string
  people: number
}

interface OrgChartResp {
  // staffed/people_here sao da s60c: a API da s60b nao os manda (o site publica antes da API -- janela de versoes)
  units: { id: string; name: string; parent_id: string | null; jobs: JobResp[]; staffed?: StaffedResp[]; people_here?: number; is_advisory?: boolean }[]
  jobs_without_area: JobResp[]
  people_without_job: number
  payroll_base_date: string | null
  can_see_names: boolean
  bands?: { id: string; name: string; position: number }[]
  functions?: { id: string; name: string; band_id: string }[]
}

const cargo = (j: JobResp): OrgChartJob => ({
  jobId: j.job_id,
  jobName: j.job_name,
  people: j.people,
  reportsToJobId: j.reports_to_job_id ?? null,
  reportsToJobName: j.reports_to_job_name ?? null,
  pendingAreaName: j.pending_area_name ?? null,
  functionId: j.function_id ?? null,
  functionName: j.function_name ?? null,
  bandId: j.band_id ?? null,
})

export async function fetchOrgChart(): Promise<OrgChart> {
  const r = await apiFetch<OrgChartResp>('/api/org-chart')
  return {
    units: r.units.map((u) => {
      const jobs = u.jobs.map(cargo)
      return {
        id: u.id,
        name: u.name,
        parentId: u.parent_id,
        jobs,
        staffed: (u.staffed ?? []).map((s) => ({ jobId: s.job_id, jobName: s.job_name, people: s.people })),
        // API antiga (sem lotacao): a conta volta a ser a da s60b (soma dos cargos da area)
        peopleHere: u.people_here ?? jobs.reduce((s, j) => s + j.people, 0),
        isAdvisory: u.is_advisory === true,
      }
    }),
    jobsWithoutArea: r.jobs_without_area.map(cargo),
    peopleWithoutJob: r.people_without_job,
    payrollBaseDate: r.payroll_base_date,
    canSeeNames: r.can_see_names === true,
    bands: [...(r.bands ?? [])].sort((a, b) => a.position - b.position).map((b) => ({ id: b.id, name: b.name, position: b.position })),
    functions: (r.functions ?? []).map((f) => ({ id: f.id, name: f.name, bandId: f.band_id })),
  }
}

/**
 * Monta a arvore pelas maes. Unidade cuja mae nao veio (inativa, ou ciclo de dado antigo) vira RAIZ -- aparece,
 * nunca some (A7 da s47). Irmaos em ordem de nome pt-BR.
 */
export function buildOrgTree(units: readonly OrgChartUnit[]): OrgChartNode[] {
  const ids = new Set(units.map((u) => u.id))
  const filhos = new Map<string | null, OrgChartUnit[]>()
  for (const u of units) {
    const mae = u.parentId !== null && ids.has(u.parentId) && u.parentId !== u.id ? u.parentId : null
    filhos.set(mae, [...(filhos.get(mae) ?? []), u])
  }
  const vistos = new Set<string>()
  const monta = (u: OrgChartUnit, depth: number): OrgChartNode => {
    vistos.add(u.id)
    const children = (filhos.get(u.id) ?? [])
      .filter((c) => !vistos.has(c.id))
      .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
      .map((c) => monta(c, depth + 1))
    return { unit: u, children, depth, total: u.peopleHere + children.reduce((s, c) => s + c.total, 0) }
  }
  const raizes = [...(filhos.get(null) ?? [])].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')).map((u) => monta(u, 0))
  // Ciclo na arvore (A -> B -> A, sem raiz): o que nao foi visitado vira raiz, para nao sumir.
  for (const u of units) if (!vistos.has(u.id)) raizes.push(monta(u, 0))
  return raizes
}

/** s60c / O3: cargo com o recuo dentro da caixa. `outsideBoss` = o chefe esta em OUTRA area ("responde a ..."). */
export interface IndentedJob {
  job: OrgChartJob
  level: number
  outsideBoss: string | null
}

/**
 * Ordena os cargos de UMA caixa pela subordinacao (reports_to_job_id): chefe primeiro, subordinados logo abaixo com
 * um nivel a mais. Chefe que nao esta na caixa -> o cargo vira topo e ganha "responde a <chefe>". Ciclo de dado
 * (A chefe de B chefe de A) nao trava nem some: o que sobrar entra como topo.
 */
export function indentJobs(jobs: readonly OrgChartJob[]): IndentedJob[] {
  const aqui = new Set(jobs.map((j) => j.jobId))
  const porChefe = new Map<string | null, OrgChartJob[]>()
  for (const j of jobs) {
    const chefe = j.reportsToJobId !== null && aqui.has(j.reportsToJobId) && j.reportsToJobId !== j.jobId ? j.reportsToJobId : null
    porChefe.set(chefe, [...(porChefe.get(chefe) ?? []), j])
  }
  const saida: IndentedJob[] = []
  const vistos = new Set<string>()
  const desce = (j: OrgChartJob, level: number) => {
    if (vistos.has(j.jobId)) return
    vistos.add(j.jobId)
    const fora = level === 0 && j.reportsToJobId !== null && !aqui.has(j.reportsToJobId) ? j.reportsToJobName : null
    saida.push({ job: j, level, outsideBoss: fora })
    for (const f of porChefe.get(j.jobId) ?? []) desce(f, level + 1)
  }
  for (const j of porChefe.get(null) ?? []) desce(j, 0)
  for (const j of jobs) desce(j, 0)
  return saida
}

/**
 * s62 / C2 (Marcelo 19/10 20:06): chefias da mesma caixa LADO A LADO. Cada cargo de topo que tem subordinados na caixa
 * vira uma COLUNA (ele + os de baixo, com o recuo); os cargos avulsos (topo sem subordinados) vao juntos numa coluna
 * final. `chefe` = job_id do topo da coluna; null = coluna dos avulsos. Ordem: chefias por nome (a da API), avulsos no fim.
 */
export interface JobGroup {
  chefe: string | null
  linhas: IndentedJob[]
}

export function groupJobs(jobs: readonly OrgChartJob[]): JobGroup[] {
  const chefias: JobGroup[] = []
  const avulsos: IndentedJob[] = []
  let atual: IndentedJob[] = []
  const fecha = () => {
    if (atual.length > 1) chefias.push({ chefe: atual[0].job.jobId, linhas: atual })
    else if (atual.length === 1) avulsos.push(atual[0])
    atual = []
  }
  for (const l of indentJobs(jobs)) {
    if (l.level === 0) fecha()
    atual.push(l)
  }
  fecha()
  return avulsos.length > 0 ? [...chefias, { chefe: null, linhas: avulsos }] : chefias
}

/** s60c / O3 (visao "Cargos"): arvore de cargos da empresa inteira pela subordinacao, com a area de cada um. */
export interface JobTreeNode {
  job: OrgChartJob
  areaName: string | null
  /** s62 / C3a v2: id da unidade da area (null = cargo sem area) -- para a cor da AREA-MAE */
  areaId: string | null
  children: JobTreeNode[]
}

export function buildJobTree(d: Pick<OrgChart, 'units' | 'jobsWithoutArea'>): JobTreeNode[] {
  const area = new Map<string, string | null>()
  const areaId = new Map<string, string | null>()
  const todos: OrgChartJob[] = []
  for (const u of d.units) for (const j of u.jobs) {
    area.set(j.jobId, u.name)
    areaId.set(j.jobId, u.id)
    todos.push(j)
  }
  for (const j of d.jobsWithoutArea) {
    area.set(j.jobId, null)
    areaId.set(j.jobId, null)
    todos.push(j)
  }
  const ids = new Set(todos.map((j) => j.jobId))
  const porChefe = new Map<string | null, OrgChartJob[]>()
  for (const j of todos) {
    const chefe = j.reportsToJobId !== null && ids.has(j.reportsToJobId) && j.reportsToJobId !== j.jobId ? j.reportsToJobId : null
    porChefe.set(chefe, [...(porChefe.get(chefe) ?? []), j])
  }
  const nome = (a: OrgChartJob, b: OrgChartJob) => a.jobName.localeCompare(b.jobName, 'pt-BR')
  const vistos = new Set<string>()
  const monta = (j: OrgChartJob): JobTreeNode => {
    vistos.add(j.jobId)
    const children = [...(porChefe.get(j.jobId) ?? [])].filter((f) => !vistos.has(f.jobId)).sort(nome).map(monta)
    return { job: j, areaName: area.get(j.jobId) ?? null, areaId: areaId.get(j.jobId) ?? null, children }
  }
  const raizes = [...(porChefe.get(null) ?? [])].sort(nome).map(monta)
  for (const j of [...todos].sort(nome)) if (!vistos.has(j.jobId)) raizes.push(monta(j))
  return raizes
}

/** s60c / O2: soma, por cargo, das pessoas LOTADAS fora da area-casa (o que aparece em `staffed` das outras caixas). */
export function staffedElsewhere(units: readonly OrgChartUnit[]): Map<string, number> {
  const m = new Map<string, number>()
  for (const u of units) for (const s of u.staffed) m.set(s.jobId, (m.get(s.jobId) ?? 0) + s.people)
  return m
}

export interface OrgChartPerson {
  id: string
  name: string
}

export async function fetchOrgChartPeople(jobId: string): Promise<OrgChartPerson[]> {
  const r = await apiFetch<{ job_id: string; people: OrgChartPerson[] }>(
    `/api/org-chart/jobs/${encodeURIComponent(jobId)}/people`,
  )
  return r.people.map((p) => ({ id: p.id, name: p.name }))
}

/** "2026-06-18" -> "18/06/2026" (sem fuso: e uma data, nao um instante). */
export function dataBr(iso: string): string {
  const [a, m, d] = iso.split('-')
  return `${d}/${m}/${a}`
}

export const ORG_CHART_QUERY_KEY = ['org-chart'] as const
export const orgChartPeopleKey = (jobId: string) => ['org-chart', 'people', jobId] as const

// ------------------------------------------------------------------ s62 / C3a: organograma de CARGOS em bandas
/**
 * Decisao de Marcelo (19/10/2026 21:21 e 21:27): "C3 = A ; D1 campo ; D2 area-mae ; D3 Fabrica" e "APROVADO A ; B1 banda ;
 * B2 lista fixa ; B3 letra so com salario ; B4 ADR". A BANDA e a linha do organograma e e do modulo de CARGOS; o GRAU
 * (letra A..H) e do modulo de SALARIO e mora dentro da banda. Lista fixa da metodologia Gente (modelo da LUTEPEL).
 * No C3a a banda e SUGERIDA pelo titulo do cargo; no C3b ela passa a ser gravada no cargo e corrigida pelo consultor.
 */
export const BANDS = ['direcao', 'gerencia_executiva', 'gerencia', 'media_lideranca', 'lideranca_operacional', 'operacional'] as const
export type BandKey = (typeof BANDS)[number]

const semAcento = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase()

/** Banda sugerida pelo TITULO do cargo (palavra-chave). Sem palavra conhecida -> operacional. */
export function suggestBand(jobName: string): BandKey {
  const n = ` ${semAcento(jobName).replace(/[^A-Z0-9]+/g, ' ')} `
  if (/ (DIRETOR|DIRETORA|PRESIDENTE|CEO|SOCIO|SOCIA) /.test(n)) return 'direcao'
  if (/ (GERENTE (EXECUTIVO|EXECUTIVA|GERAL)|SUPERINTENDENTE) /.test(n)) return 'gerencia_executiva'
  if (/ GERENTE /.test(n)) return 'gerencia'
  if (/ (COORDENADOR|COORDENADORA|SUPERVISOR|SUPERVISORA|ENCARREGADO|ENCARREGADA|ESPECIALISTA) /.test(n)) return 'media_lideranca'
  if (/ (ANALISTA|CONDUTOR|CONDUTORA|LIDER|EXECUTIVO|EXECUTIVA) /.test(n)) return 'lideranca_operacional'
  return 'operacional'
}

export interface PlacedJob {
  job: OrgChartJob
  areaName: string | null
  areaId: string | null
  /** indice em BANDS (0 = topo) */
  band: number
  /** centro, em colunas a partir de 0 */
  x: number
  /** posicao dentro de uma PILHA (subordinados todos sem subordinados, mesma banda); 0 fora de pilha */
  k: number
  parentId: string | null
  stacked: boolean
}

export interface BandLayout {
  placed: PlacedJob[]
  /** n. de colunas ocupadas */
  cols: number
  /** linhas de cartao por banda (pilha maior da banda; minimo 1) */
  rows: number[]
}

/**
 * s69 / 2u (Marcelo 29/10/2026 14:35, "os que estao abaixo de coordenador financeiro estao empilhados e nao lado a
 * lado"; 14:42 "vamos fazer agora"): ate 4 subordinados ficam LADO A LADO, cada um na sua coluna, com a sua linha.
 * A PILHA so a partir de 5 -- e o caso da LUTEPEL (40 operacionais nao viram 40 colunas).
 */
export const PILHA_A_PARTIR = 5

/**
 * Posiciona a arvore de cargos: y = BANDA do cargo (nunca acima da do chefe: subordinado fica pelo menos uma banda
 * abaixo), x = centro das folhas do galho (arvore arrumada). Chefe com PILHA_A_PARTIR ou mais subordinados, TODOS sem
 * subordinados e da MESMA banda, vira PILHA (uma coluna so, um embaixo do outro) -- como as listas do modelo da
 * LUTEPEL; sem isso, 40 cargos operacionais virariam 40 colunas.
 */
export function layoutJobBands(
  d: Pick<OrgChart, 'units' | 'jobsWithoutArea'>,
  nBands: number = BANDS.length,
  bandOf: (j: OrgChartJob) => number = (j) => BANDS.indexOf(suggestBand(j.jobName)),
): BandLayout {
  const raizes = buildJobTree(d)
  const placed: PlacedJob[] = []
  const rows = Array.from({ length: Math.max(1, nBands) }, () => 1)
  const ultima = rows.length - 1
  const banda = (n: JobTreeNode, mae: number) => Math.min(ultima, Math.max(bandOf(n.job), mae + 1))
  const largura = (n: JobTreeNode, b: number): number => {
    if (n.children.length === 0) return 1
    const bs = n.children.map((f) => banda(f, b))
    if (n.children.length >= PILHA_A_PARTIR && n.children.every((f) => f.children.length === 0) && bs.every((x) => x === bs[0])) return 1
    return n.children.reduce((s, f, i) => s + largura(f, bs[i]), 0)
  }
  const coloca = (n: JobTreeNode, b: number, inicio: number, mae: string | null) => {
    const w = largura(n, b)
    const x = inicio + w / 2
    placed.push({ job: n.job, areaName: n.areaName, areaId: n.areaId, band: b, x, k: 0, parentId: mae, stacked: false })
    const bs = n.children.map((f) => banda(f, b))
    const pilha = n.children.length >= PILHA_A_PARTIR && n.children.every((f) => f.children.length === 0) && bs.every((v) => v === bs[0])
    if (pilha) {
      n.children.forEach((f, k) => placed.push({ job: f.job, areaName: f.areaName, areaId: f.areaId, band: bs[0], x, k, parentId: n.job.jobId, stacked: true }))
      rows[bs[0]] = Math.max(rows[bs[0]], n.children.length)
      return
    }
    let o = inicio
    n.children.forEach((f, i) => {
      coloca(f, bs[i], o, n.job.jobId)
      o += largura(f, bs[i])
    })
  }
  let o = 0
  for (const r of raizes) {
    const b = Math.min(ultima, Math.max(0, bandOf(r.job)))
    coloca(r, b, o, null)
    o += largura(r, b)
  }
  return { placed, cols: o, rows }
}

/**
 * s62 / C3a v2 -- cor da AREA-MAE (marca v1.6.0, paleta da Fabrica da Marca em brand/paleta_areas/): a area-mae e a
 * unidade logo abaixo da raiz (DIRECAO). Ordem = a da arvore (raizes e filhas por nome). 1a area-mae = cor 1; da 9a em
 * diante recomeca. Filhas herdam a cor da mae. Devolve, por unidade: o indice da cor (0..7) ou null para a propria raiz.
 */
export function areaMotherIndex(units: readonly OrgChartUnit[]): Map<string, number | null> {
  const m = new Map<string, number | null>()
  let ordem = 0
  const desce = (n: OrgChartNode, cor: number | null) => {
    m.set(n.unit.id, cor)
    for (const f of n.children) desce(f, cor)
  }
  for (const r of buildOrgTree(units)) {
    // topo em CADEIA (ex.: ACIONISTAS -> DIRETORIA EXECUTIVA, uma filha so) conta como a Direcao: faixa navy; as cores
    // comecam no primeiro nivel que se abre em mais de uma area (s62, mapa no modelo da XTURBO)
    let topo = r
    m.set(topo.unit.id, null)
    while (topo.children.length === 1 && topo.children[0].children.length > 0) {
      topo = topo.children[0]
      m.set(topo.unit.id, null)
    }
    for (const f of topo.children) desce(f, ordem++ % 8)
  }
  return m
}

/**
 * s62 / MAPA DE AREAS no modelo da XTURBO (Marcelo 20/10/2026 13:40 "pensei em algo tipo o anexo"; 13:44 "nao pintar
 * dentro, cor so na faixa superior"): blocos ENCAIXADOS, sem linhas -- cada area ocupa exatamente a largura das filhas,
 * que ficam logo abaixo, encostadas. Unidade de largura = 1 coluna por area FOLHA (sem filhas). Folha = coluna estreita
 * com o nome na vertical. Devolve, por area: coluna inicial, n. de colunas, nivel e se e folha.
 */
export interface AreaBlock {
  id: string
  parentId: string | null
  depth: number
  col: number
  cols: number
  leaf: boolean
  /** s62: posicao numa pilha de folhas irmas -- s69 / 2l: as irmas ficam lado a lado, entao sempre 0 / false */
  k: number
  stacked: boolean
}

/**
 * Regra de largura -- s69 / 2l (Marcelo 27/10/2026 17:21, "Q2 lado a lado"; substitui a pilha de 20/10 14:14): areas
 * IRMAS ficam LADO A LADO, cada uma na sua coluna, logo abaixo da mae. Largura da folha = 1; da mae = soma das filhas.
 * O nome continua na horizontal (a coluna tem a largura da maior palavra). `k`/`stacked` ficam 0/false (compatibilidade).
 */
export function layoutAreaMap(roots: readonly OrgChartNode[]): { blocks: AreaBlock[]; cols: number; depth: number; stack: number[] } {
  const blocks: AreaBlock[] = []
  const stack: number[] = []
  let fundo = 0
  const largura = (n: OrgChartNode): number =>
    n.children.length === 0 ? 1 : Math.max(1, n.children.reduce((s, f) => s + largura(f), 0))
  const coloca = (n: OrgChartNode, d: number, col: number, mae: string | null) => {
    blocks.push({ id: n.unit.id, parentId: mae, depth: d, col, cols: largura(n), leaf: n.children.length === 0, k: 0, stacked: false })
    fundo = Math.max(fundo, d + 1)
    let c = col
    for (const f of n.children) {
      coloca(f, d + 1, c, n.unit.id)
      c += largura(f)
    }
  }
  let c = 0
  for (const r of roots) {
    coloca(r, 0, c, null)
    c += largura(r)
  }
  for (let d = 0; d < fundo; d++) stack[d] = Math.max(1, stack[d] ?? 1)
  return { blocks, cols: c, depth: fundo, stack }
}

// ------------------------------------------------------------------ s62 / C3b-2: faixa pela FUNCAO do cargo
/**
 * ADR-0032 (Marcelo 20/10/2026): o encaixe do cargo na linha vem da FUNCAO gravada no cargo. Sem funcao gravada, a faixa
 * da funcao SUGERIDA pelo titulo (`suggestFunction`), marcada como sugerida; sem sugestao, a ultima faixa. Com a API
 * anterior (sem `bands`), volta a regra da C3a (palavra-chave do titulo nas 6 faixas fixas).
 */
export interface BandRow {
  key: string
  label: string
}

export interface BandPlan {
  rows: BandRow[]
  /** indice da linha do cargo e se veio de SUGESTAO (sem funcao gravada) */
  bandOf: (j: OrgChartJob) => number
  suggested: (j: OrgChartJob) => boolean
}

export function planBands(
  d: Pick<OrgChart, 'bands' | 'functions'>,
  fixedLabel: (k: BandKey) => string,
  suggest: (jobName: string, fs: readonly OrgChartFunction[]) => OrgChartFunction | null,
): BandPlan {
  const bands = d.bands ?? []
  if (bands.length === 0) {
    return {
      rows: BANDS.map((k) => ({ key: k, label: fixedLabel(k) })),
      bandOf: (j) => BANDS.indexOf(suggestBand(j.jobName)),
      suggested: () => false,
    }
  }
  const idx = new Map(bands.map((b, i) => [b.id, i]))
  const fs = d.functions ?? []
  const gravada = (j: OrgChartJob) => (j.bandId ? idx.get(j.bandId) : undefined)
  return {
    rows: bands.map((b) => ({ key: b.id, label: b.name })),
    bandOf: (j) => {
      const g = gravada(j)
      if (g !== undefined) return g
      const s = suggest(j.jobName, fs)
      return s ? (idx.get(s.bandId) ?? bands.length - 1) : bands.length - 1
    },
    suggested: (j) => gravada(j) === undefined,
  }
}

/** X3: tira da arvore as areas de ASSESSORIA (com o que estiver abaixo delas) e devolve-as a parte, na ordem da arvore. */
export function splitAdvisory(roots: readonly OrgChartNode[]): { main: OrgChartNode[]; advisory: OrgChartNode[] } {
  const advisory: OrgChartNode[] = []
  const poda = (n: OrgChartNode): OrgChartNode => ({
    ...n,
    children: n.children.filter((f) => {
      if (f.unit.isAdvisory) {
        advisory.push(f)
        return false
      }
      return true
    }).map(poda),
  })
  const main = roots.filter((r) => {
    if (r.unit.isAdvisory) {
      advisory.push(r)
      return false
    }
    return true
  }).map(poda)
  return { main, advisory }
}

/** X3: grava a marca de ASSESSORIA da area (PATCH /api/organization-units/{id}/advisory; organization_core.unit.write). */
export async function setUnitAdvisory(unitId: string, isAdvisory: boolean): Promise<void> {
  await apiFetch<unknown>(`/api/organization-units/${encodeURIComponent(unitId)}/advisory`, {
    method: 'PATCH',
    body: { is_advisory: isAdvisory },
  })
}

// ------------------------------------------------------------------ s62: nome da area sem cortar palavra no meio
/**
 * Marcelo (20/10/2026 16:12): "algumas estao cortando no meio, deixe uma largura minima que caiba uma palavra grande".
 * Medida do texto do mapa (Inter 12 px, negrito, MAIUSCULAS): ~8,6 px por letra, com folga. A coluna do mapa passa a ter
 * no minimo a MAIOR palavra dos nomes + o respiro interno; nunca menos que os 112 px de antes.
 */
export const MAPA_CHAR_PX = 8.6
export const MAPA_PAD = 12
export const MAPA_COL_MIN = 112

export function mapColumnWidth(names: readonly string[]): number {
  const maior = Math.max(0, ...names.flatMap((n) => n.split(/\s+/)).map((w) => w.length))
  return Math.max(MAPA_COL_MIN, Math.ceil(maior * MAPA_CHAR_PX + MAPA_PAD + 4))
}

/** Quantas linhas o nome ocupa numa largura (quebra SO entre palavras). */
export function wrapLines(name: string, px: number): number {
  const cabe = Math.max(1, Math.floor(px / MAPA_CHAR_PX))
  let linhas = 0
  let atual = 0
  for (const w of name.split(/\s+/).filter((x) => x !== '')) {
    if (atual === 0) {
      linhas += 1
      atual = w.length
    } else if (atual + 1 + w.length <= cabe) atual += 1 + w.length
    else {
      linhas += 1
      atual = w.length
    }
  }
  return Math.max(1, linhas)
}
